<template>
    <TransitionGroup :name="listName" tag="ul" class="upload-file-list el-upload-list el-upload-list--text" :class="{ [levelClass]: true, ...finalCustomClass, 'none-space': noneSpace }" :style="{ 'max-height': finalMaxHeight }">
        <li tabindex="0" class="upload-file-list--item el-upload-list-item el-upload-list__item is-ready"
          v-for="file in files" :key="fileKey(file)">
            <upload-file-item v-on="itemClickListenerCallback(file)" :file="file" :actions="actions" :deep="deep" :hide-default-actions="hideDefaultActions || hideDefaultItemActionsCallback(file)" :hide-expand-action="hideExpandAction || hideExpandItemActionsCallback(file)" :reverse-colors="reverseColors"
             :custom="custom" :list-name="listName" :hide-status="hideStatus || hideStatusItemCallback(file)" :status-callbacks="statusCallbacks" @remove="handleRemove" />
        </li>
    </TransitionGroup>
</template>

<script>
import { isAssignableTo } from '@/services/functions/isAssignableTo'

// #region Classes && Methods
class FileItem {
  /** @type {String} */ name
  /** @type {Number} */ percentage
  /** @type {File} */ raw
  /** @type {Number} */ size
  /** @type {String} */ status
  /** @type {Number} */ uid
  /** @type {Boolean} */ loading
  /** @type {String} */ tooltip
  /** @type {Array<FileItem>} */ files
}
class Action {
  /** @type {function(FileItem): void} */ handler
  /** @type {function(FileItem): boolean} */ show
  /** @type {String} */ name
}
class StatusCallback {
  /** @type {function(FileItem): boolean} */ loading
  /** @type {function(FileItem): boolean} */ success
  /** @type {function(FileItem): boolean} */ warning
  /** @type {function(FileItem): boolean} */ error
}
// #endregion

export default {
  name: 'upload-file-list',
  components: {
    'upload-file-item': () => import('@/components/UploadFileItem.vue'),
  },
  props: {
    files: {
      type: Array,
      required: true,
      validator: value => value.every(item => isAssignableTo(item, FileItem))
    },
    actions: {
      type: Array,
      default: () => [],
      validator: value => value.every(item => isAssignableTo(item, Action))
    },
    maxHeight: {
      type: [String, Number],
      default: '100px'
    },
    listName: {
      type: String,
      default: 'el-list'
    },
    deep: {
      type: [Number, Boolean],
      default: false
    },
    hideDefaultActions: {
      type: Boolean,
      default: false
    },
    hideExpandAction: {
      type: Boolean,
      default: false
    },
    hideStatus: {
      type: Boolean,
      default: false
    },
    hideDefaultItemActionsCallback: {
      type: Function,
      default: efile => false
    },
    hideExpandItemActionsCallback: {
      type: Function,
      default: efile => false
    },
    hideStatusItemCallback: {
      type: Function,
      default: efile => false
    },
    statusCallbacks: {
      type: StatusCallback,
      default: () => ({})
    },
    itemClickListenerCallback: {
      type: Function,
      default: function (efile) {
        return this.clickListener
      }
    },
    noneSpace: {
      type: Boolean,
      default: false,
    },
    reverseColors: {
      type: Boolean,
      default: false,
    },
    custom: {
      type: [String, Object],
      default: ''
    },
  },
  data () {
    return {
      levelClass: '',
    }
  },
  mounted () {
    this.applyLevelClass()
  },
  watch: {
    reverseColors: function (value, oldValue) {
      if (value !== oldValue) {
        this.applyLevelClass()
      }
    }
  },
  computed: {
    finalCustomClass: function () {
      if (typeof this.custom === 'string') {
        if (this.custom.trim() === '') {
          return {}
        }
        return { [this.custom]: true }
      }
      return this.custom
    },
    finalMaxHeight: function () {
      if (typeof this.maxHeight === 'number') {
        return `${this.maxHeight}px !important`
      }
      return `${this.maxHeight} !important`
    },
    clickListener: function () {
      return this.$listeners.click ? { click: this.$listeners.click } : {}
    },
  },
  methods: {
    handleRemove: function (file) {
      this.$emit('remove-file', file)
    },
    fileKey: function (file) {
      var filekey = `${file.uid}.${file.name}.${file.origin}`
      if (file.linkedfile) {
        filekey = `${file.linkedfile.linkid}.${file.linkedfile.uid}.${file.name}.${file.origin}`
      } else if (file.linkid) {
        filekey = `${file.linkid}.${file.name}.${file.origin}`
      }
      return filekey
    },
    countUploadFileListAncestors () {
      var count = 0
      var parent = this.$parent
      while (parent) {
        if (parent.$options.name === 'upload-file-list') {
          count++
        }
        parent = parent.$parent
      }
      return count
    },
    applyLevelClass () {
      const ancestorCount = this.countUploadFileListAncestors()
      if (this.reverseColors) {
        this.levelClass = ancestorCount % 2 !== 0 ? 'even-level' : 'odd-level'
      } else {
        this.levelClass = ancestorCount % 2 === 0 ? 'even-level' : 'odd-level'
      }
    },
  },
}
</script>

<style scoped>
.el-upload-list.none-space > .upload-file-list--item:first-child {
  margin-top: 5px;
}
.el-upload-list {
  padding-bottom: 10px;
}
.el-upload-list.none-space {
  padding-bottom: 5px;
}

.upload-file-list.even-level > .upload-file-list--item:hover {
  background-color: var(--secondary-50);
 }
 
 .dark .upload-file-list.even-level > .upload-file-list--item:hover {
  background-color: var(--darkblue-200);
 }
 
  .upload-file-list.odd-level > .upload-file-list--item:hover {
   background-color: var(--secondary-100);
  }
  .dark .upload-file-list.odd-level > .upload-file-list--item:hover {
   background-color: var(--darkblue-100);
  }
</style>
